import { useState, useRef, useCallback, useEffect } from "react";
import { clamp } from "utils/math/clamp";
import type { ViewBox } from "types/documentation";
import type { UseSvgViewBoxPan, OnMouseDown, OnMouseMove } from "./types";

export const useSvgViewBoxPan: UseSvgViewBoxPan = ({
  svgRef,
  viewBoxRef,
  baseViewBox,
  isDisabled,
  onPan,
}) => {
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef<{ x: number; y: number; vb: ViewBox } | null>(null);

  const stopPanning = useCallback(() => setIsPanning(false), []);

  const onMouseDown: OnMouseDown = useCallback(
    (e) => {
      if (isDisabled) return;

      panStart.current = {
        x: e.clientX,
        y: e.clientY,
        vb: { ...viewBoxRef.current },
      };
      setIsPanning(true);
    },
    [isDisabled, viewBoxRef]
  );

  const onMouseMove: OnMouseMove = useCallback(
    (e) => {
      if (!isPanning || !panStart.current || !svgRef.current) return;

      const rect = svgRef.current.getBoundingClientRect();
      const start = panStart.current;

      const dx = ((e.clientX - start.x) / rect.width) * start.vb.w;
      const dy = ((e.clientY - start.y) / rect.height) * start.vb.h;

      const newX = clamp({
        value: start.vb.x - dx,
        min: baseViewBox.x,
        max: baseViewBox.w - start.vb.w,
      });

      const newY = clamp({
        value: start.vb.y - dy,
        min: baseViewBox.y,
        max: baseViewBox.h - start.vb.h,
      });

      onPan({ ...start.vb, x: newX, y: newY });
    },
    [isPanning, svgRef, baseViewBox, onPan]
  );

  useEffect(() => {
    if (isDisabled && isPanning) {
      setIsPanning(false);
    }
  }, [isDisabled, isPanning]);

  return {
    isPanning,
    onMouseDown,
    onMouseMove,
    onMouseUp: stopPanning,
    onMouseLeave: stopPanning,
  };
};
